import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip
} from '@mui/material';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import ImageUploadModal from '../../../../utils/ImageUploadModal';
import OrderByOptionService from '../../../setting/orderByOption/OrderByOptionService';

const PurchaseRequestModal = ({
  isOpen,
  onClose,
  item,
  onComplete,
  onSendToBot,
  style
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [orderByOptions, setOrderByOptions] = useState([]);
  const [orderByOptionId, setOrderByOptionId] = useState(null);
  const [isSeven, setIsSeven] = useState(false);
  const [sevenFee, setSevenFee] = useState('');
  const [pinnedBuyerId, setPinnedBuyerId] = useState(''); // "ส่งการ์ดให้คนนี้เท่านั้น" on the hand-over ('' = the queue)
  const [manualMode, setManualMode] = useState(false); // "แอดมินซื้อเอง": the photo + Order By + 7-fee path, a fallback when the bot can take it
  useEffect(() => { setManualMode(false); }, [item?.purchaseId]);

  // Orderer list for the "Order By" select — same source the beam confirm form uses.
  useEffect(() => {
    OrderByOptionService.getActiveOrderByOptions()
      .then(res => setOrderByOptions(res.data || []))
      .catch(() => {});
  }, []);

  // Reset the confirm-form selections when a different case is opened.
  useEffect(() => {
    setOrderByOptionId(null);
    setPinnedBuyerId('');
    setIsSeven(false);
    setSevenFee('');
    setSelectedFiles([]);
  }, [isOpen, item?.purchaseId]);

  const handleImageUploadConfirm = (files) => {
    setSelectedFiles(files);
    setShowImageUploadModal(false);
  };

  const handleImageUploadClose = () => {
    // Don't clear selectedFiles here: ImageUploadModal.handleConfirm calls
    // onConfirm() then onRequestClose(), so wiping here would immediately undo
    // the file we just accepted. Files are reset per-case in the effect below.
    setShowImageUploadModal(false);
  };

  const handleComplete = async () => {
    if (!orderByOptionId) {
      toast.error('กรุณาเลือกผู้สั่งซื้อ (Order By)');
      return;
    }
    setIsSubmitting(true);
    try {
      // Create FormData to send files
      const formData = new FormData();
      selectedFiles.forEach((file, index) => {
        formData.append('files', file);
      });

      // Add other data
      formData.append('purchaseId', item.purchaseId);
      formData.append('status', 'confirmed');
      formData.append('smileNameInput', item.smileName);
      formData.append('orderByOptionId', orderByOptionId);
      if (isSeven && String(sevenFee).trim() !== '') {
        formData.append('sevenFee', String(sevenFee).trim());
      }

      if (selectedFiles.length === 0) {
        toast.error('กรุณาอัพโหลดรูปภาพ');
        return;
      }

      await onComplete(formData);
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) {
      toast.error('กรุณาเลือกเหตุผลในการปฏิเสธ');
      return;
    }

    setIsSubmitting(true);
    try {
      await onComplete({
        purchaseId: item.purchaseId,
        status: 'cancelled',
        remarks: rejectReason
      }); 
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setShowRejectDialog(false);
      setIsSubmitting(false);
    }
  };

  // Owner 2026-11-01 (plan E): once the admin has checked the request (can it be imported, is it
  // the right item), the dispatch bot finds the buyer; the case leaves the board while the bot
  // owns it. Mercari only — PayPay is bought in-house.
  const canSendToBot = Boolean(onSendToBot) && (item?.source || 'mercari') === 'mercari';
  // Review stage: the admin checks the request, then sends it to the buyers or rejects it. The
  // manual buy (photo, Order By, 7-fee) stays reachable behind "แอดมินซื้อเอง".
  const manual = !canSendToBot || manualMode;
  const botBuyers = orderByOptions.filter((b) => Boolean(b && b.dispatchEnabled && (b.dispatchLineId || b.lineGroupId)));
  const pinnedBuyer = botBuyers.find((b) => String(b.id) === String(pinnedBuyerId)) || null;
  const handleSendToBot = async () => {
    const who = pinnedBuyer ? `ส่งการ์ดให้ ${pinnedBuyer.label} เท่านั้น` : 'ส่งให้คนกด (บอท) ตามคิว';
    if (!window.confirm(`${who} ทันที? เคสนี้จะออกจากบอร์ดจนกว่าบอทจะจบงาน`)) return;
    setIsSubmitting(true);
    try {
      await onSendToBot(item.purchaseId, pinnedBuyer ? pinnedBuyer.id : null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        ...style,
        content: {
          ...style.content,
          width: '800px',
          maxWidth: '90vw'
        }
      }}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
    >
      <Paper>
        <Box p={3}>
          {!item ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography variant="h6" color="text.secondary">
                กำลังโหลดข้อมูล...
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Chip
                  label="สั่งซื้อสินค้า"
                  color="primary" 
                  sx={{ 
                    fontSize: '1.1rem',
                    height: '32px',
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  รายละเอียดการสั่งซื้อ
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                {/* Left side - Image and URL */}
                <Grid item xs={12} md={6}>
                  <Card elevation={0} sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}>
                    <CardMedia
                      component="img"
                      image={item.imageUrl}
                      alt="Item"
                      sx={{
                        height: 300,
                        objectFit: 'contain',
                        backgroundColor: '#f5f5f5'
                      }}
                    />
                    <CardContent>
                      <Link 
                        href={item.itemUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ 
                          wordBreak: 'break-all',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {item.itemUrl}
                      </Link>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Right side - Customer Info and Amount */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    {/* Smile Account — running number for reference. JP name is no
                        longer shown (customer name isn't put on the order). */}
                    <Box mb={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Smile Account
                      </Typography>
                      <Box sx={{
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1
                      }}>
                        <Typography variant="body1" fontWeight="bold">
                          {item.smileName}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Amount */}
                    <Box mb={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        จำนวนเงิน
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h5" color="secondary.main" fontWeight="bold">
                          ¥{item.price?.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Other Customer Info */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        ข้อมูลเพิ่มเติม
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        ชื่อ: {item.nickname || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        เบอร์โทร: {item.phone || 'N/A'}
                      </Typography>
                      {item?.isCheck && (
                        <Chip
                          label="🔍 ส่งตรวจ (Anshin)"
                          sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'center',
                            mt: 1,
                            bgcolor: '#fef3c7',
                            color: '#78350f',
                            fontWeight: 700,
                            border: '2px solid #f59e0b',
                            height: 48,
                            fontSize: '1.15rem',
                            '& .MuiChip-label': { px: 2 }
                          }}
                        />
                      )}
                      {/* Shipping mode — always shown, as clearly as Anshin, so the
                          admin can't miss whether it's air or sea. */}
                      <Chip
                        label={item?.isAir ? '✈️ ทางอากาศ (Air)' : '🚢 ทางเรือ (Sea)'}
                        sx={{
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'center',
                          mt: 1,
                          bgcolor: item?.isAir ? '#dbeafe' : '#cffafe',
                          color: item?.isAir ? '#1e3a8a' : '#164e63',
                          fontWeight: 700,
                          border: item?.isAir ? '2px solid #3b82f6' : '2px solid #06b6d4',
                          height: 48,
                          fontSize: '1.15rem',
                          '& .MuiChip-label': { px: 2 }
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {manual && (<>
              {/* Order By (orderer) + optional 7-11 fee — same as the beam confirm form */}
              <Box mt={3} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="mercari-order-by-label" shrink>ผู้สั่งซื้อ (Order By)</InputLabel>
                  <Select
                    labelId="mercari-order-by-label"
                    id="orderByOptionId"
                    value={
                      orderByOptionId != null && orderByOptions.some((o) => o.id === orderByOptionId)
                        ? orderByOptionId
                        : ''
                    }
                    label="ผู้สั่งซื้อ (Order By)"
                    notched
                    displayEmpty
                    onChange={(e) => setOrderByOptionId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  >
                    <MenuItem value="" disabled>
                      <em>กรุณาเลือก</em>
                    </MenuItem>
                    {orderByOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSeven}
                        onChange={(e) => {
                          setIsSeven(e.target.checked);
                          if (!e.target.checked) setSevenFee('');
                        }}
                        color="primary"
                      />
                    }
                    label="จ่าย Seven (Seven-Eleven)"
                  />
                  {isSeven && (
                    <TextField
                      label="Seven Fee (เยน)"
                      value={sevenFee}
                      onChange={(e) => setSevenFee(e.target.value)}
                      size="small"
                      type="number"
                      sx={{ width: 160 }}
                      placeholder="0"
                    />
                  )}
                </Box>
              </Box>

              {/* File Upload Section */}
              <Box mt={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  อัพโหลดไฟล์
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  border: '1px dashed #e0e0e0',
                  borderRadius: 2,
                  textAlign: 'center'
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowImageUploadModal(true)}
                    sx={{ mb: 2 }}
                  >
                    เลือกไฟล์
                  </Button>
                  
                  {/* Show selected files count */}
                  {selectedFiles.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      เลือกไฟล์แล้ว {selectedFiles.length} ไฟล์
                    </Typography>
                  )}
                </Box>
              </Box>
              </>)}

              <Divider sx={{ my: 3 }} />

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                {canSendToBot && (
                  <Button variant="text" size="small" onClick={() => setManualMode((m) => !m)} disabled={isSubmitting} sx={{ mr: 'auto' }}>
                    {manualMode ? 'กลับไปส่งให้คนกด' : 'แอดมินซื้อเอง'}
                  </Button>
                )}
                {manual && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleComplete}
                    disabled={isSubmitting}
                    sx={{ minWidth: '100px' }}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : 'เสร็จสิ้น'}
                  </Button>
                )}
                {canSendToBot && !manual && botBuyers.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="pinned-buyer-label">ส่งการ์ดให้</InputLabel>
                    <Select labelId="pinned-buyer-label" label="ส่งการ์ดให้" value={pinnedBuyerId} onChange={(e) => setPinnedBuyerId(e.target.value)} disabled={isSubmitting}>
                      <MenuItem value="">ตามคิวบอท</MenuItem>
                      {botBuyers.map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.label} เท่านั้น</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                {canSendToBot && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSendToBot}
                    disabled={isSubmitting}
                    sx={{ minWidth: '120px' }}
                  >
                    ส่งให้คนกด
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={isSubmitting}
                  sx={{ minWidth: '100px' }}
                >
                  ปฏิเสธ
                </Button>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={isSubmitting}
                  sx={{ minWidth: '100px' }}
                >
                  ปิด
                </Button>                
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageUploadModal}
        onRequestClose={handleImageUploadClose}
        onConfirm={handleImageUploadConfirm}
        title="อัพโหลดรูปภาพ"
        maxFiles={1}
        acceptedFileTypes={['image/*']}
        maxFileSize={2 * 1024 * 1024} // 5MB
      />

      {/* Reject Dialog */}
      <Modal
        isOpen={showRejectDialog}
        onRequestClose={() => setShowRejectDialog(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '400px',
            maxWidth: '90%',
            padding: '20px',
            borderRadius: '8px'
          }
        }}
        ariaHideApp={false}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            เลือกเหตุผลในการปฏิเสธ
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>เหตุผล</InputLabel>
            <Select
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              label="เหตุผล"
            >
              <MenuItem value="item_sold">สินค้าขายออกไปแล้ว</MenuItem>
              <MenuItem value="price_changed">ราคาสินค้าเปลี่ยนแปลง / สูงกว่าที่ตกลง</MenuItem>
              <MenuItem value="exclusive_item">เป็นสินค้าเฉพาะของลูกค้าท่านอื่น</MenuItem>
              <MenuItem value="prohibited_item">สินค้าต้องห้าม / นำเข้าไม่ได้</MenuItem>
            </Select>
          </FormControl>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={!rejectReason || isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'ยืนยันการปฏิเสธ'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowRejectDialog(false)}
              disabled={isSubmitting}
            >
              ยกเลิก
            </Button>
          </Box>
        </Box>
      </Modal>
    </Modal>
  );
};

export default PurchaseRequestModal;
